import React, { useState, useCallback, useEffect, ReactNode } from "react";
import { useTranslation } from "react-i18next";
import clsx from "clsx";
import { isEqual } from "lodash-es";
interface CheckBoxProps {
  maxSelectable?: number;
  title?: string;
  titleProp?: any;
  options?: OptionProps[];
  defaultSelected?: OptionPropsValue[];
  pageSize?: number;
  onSelect?: (selected: OptionProps[]) => void;
  classNameProp?: any;
  children?: (option: OptionProps, index?: number) => React.ReactNode;
  groupNodes?: Record<string, ReactNode>;
  disabled?: boolean;
}

const CheckBox: React.FC<CheckBoxProps> = ({
  maxSelectable,
  title,
  options = [],
  defaultSelected = [],
  pageSize,
  classNameProp,
  children,
  onSelect,
  groupNodes,
  titleProp,
  disabled,
}) => {
  const [seeMore, setSeeMore] = useState(false);
  const { t } = useTranslation();
  const handleButtonClick = useCallback(
    (option: OptionProps) => {
      if (option.disabled) return; // Return early if the option is disabled
      const isSelected = defaultSelected?.some((selected) =>
        isEqual(selected, option.value),
      );
      let newSelectedButtons = isSelected
        ? options.filter((item) => {
            return defaultSelected
              .filter((selected) => !isEqual(selected, option.value))
              .some((selected) => isEqual(selected, item.value));
          })
        : [
            ...options.filter((item) => defaultSelected.includes(item.value)),
            option,
          ]; // Otherwise, add the option if it's not selected
      if (maxSelectable && newSelectedButtons.length > maxSelectable) {
        newSelectedButtons = newSelectedButtons.slice(maxSelectable);
      }

      onSelect?.(newSelectedButtons);
    },
    [defaultSelected, maxSelectable, onSelect], // Add `selectedButtons` as a dependency
  );

  const isButtonSelected = useCallback(
    (option: OptionProps) => {
      if (!defaultSelected) return false;
      return defaultSelected?.some((selected) =>
        isEqual(selected, option.value),
      );
    },
    [defaultSelected],
  );
  const groups = options
    .map((x) => x.group ?? "")
    .filter((x, index, a) => {
      return a.indexOf(x) === index;
    });

  return (
    <div>
      {title && (
        <p
          {...titleProp}
          className="mb-4 text-sm font-normal text-[#5E6D77] sm:text-base sm:leading-[1.5rem]"
        >
          {title}
        </p>
      )}
      <div className="flex flex-col gap-4">
        {groups.map((group) => (
          <div key={group} className="flex flex-wrap gap-4">
            {groupNodes?.[group] ?? null}
            {options
              .filter((opt) => (opt.group ?? "") === group)
              .filter(
                (option, index) =>
                  (pageSize && index < pageSize) || seeMore || !pageSize,
              )
              .map((option, index) => {
                const isSelected = isButtonSelected(option);
                const isDisabled = option.disabled;

                return (
                  <button
                    type="button"
                    key={JSON.stringify(option.value) ?? ""}
                    {...classNameProp}
                    className={clsx(
                      "checkbox-btn rounded-[0.3125rem] border px-4 py-[7px] text-sm leading-[1.3619rem] sm:text-base sm:font-normal",
                      isSelected
                        ? "border-[#007AFF] bg-[#DBE7FF] text-[#007AFF]"
                        : "border-[#232323] bg-white text-[#232323]",
                      isDisabled &&
                        "cursor-not-allowed border-[#BFC5C9] text-[#BFC5C9]",
                      option.invisible && "hidden",
                    )}
                    onClick={() => handleButtonClick(option)}
                    disabled={disabled || isDisabled}
                  >
                    {children ? children(option, index) : option.displayName}
                  </button>
                );
              })}
          </div>
        ))}
      </div>

      {pageSize && options.length > pageSize && !seeMore && (
        <button
          className="flex items-center gap-[0.3125rem]"
          onClick={() => setSeeMore(true)}
        >
          <span className="text-base font-semibold leading-[1.25rem]">
            {t("See more")}
          </span>
          <img
            className="h-3 w-3"
            src="../assets/icons/arrow-down.svg"
            alt=""
          />
        </button>
      )}
    </div>
  );
};

export default CheckBox;
